import { useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { replyToRetiredSupporter } from "@/lib/api";

/**
 * Shared reply-to-retired-helper dialog. Renders its own trigger (children pattern).
 * Props:
 *  - letterId, ownerToken, index — identify which retired supporter is being replied to
 *  - retired — the retired supporter object (name, thank_you_message, reply_sent_at, ...)
 *  - onReplied(updatedRetiredEntry) — parent callback so it can refresh chips/badges
 *  - children — the trigger element (chip, button, etc.)
 *  - testIdPrefix — for stable data-testids on dialog controls
 */
export function RetiredNoteReply({
  letterId, ownerToken, index, retired, onReplied, children, testIdPrefix = "retired-reply",
}) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const alreadyReplied = Boolean(retired?.reply_sent_at);
  const name = retired?.name || "your earlier helper";

  const send = async () => {
    const trimmed = message.trim();
    if (!trimmed) { toast.error("Please write a short note first."); return; }
    if (sending) return;
    setSending(true);
    try {
      const r = await replyToRetiredSupporter(letterId, ownerToken, index, trimmed);
      toast.success(`Sent to ${name}.`);
      const updated = (r?.notifications?.retired_supporters || [])[index];
      onReplied?.(updated);
      setMessage("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't send that note right now.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Reply to {name}</DialogTitle>
          <DialogDescription>
            {alreadyReplied
              ? "You already sent a thank-you. Feel free to send another if you'd like."
              : `A short thank-you goes a long way. We'll email it straight to ${name}.`}
          </DialogDescription>
        </DialogHeader>

        {retired?.thank_you_message && (
          <div className="rounded-2xl bg-moss-50 border border-moss-200 p-4">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-moss-700">
              <MessageCircle strokeWidth={2.5} className="h-3.5 w-3.5" />
              What {name} wrote
            </p>
            <p className="mt-2 text-base leading-relaxed text-slate-800 whitespace-pre-wrap">
              {retired.thank_you_message}
            </p>
          </div>
        )}

        <div className="mt-3">
          <label className="text-sm font-semibold text-slate-800" htmlFor={`${testIdPrefix}-textarea`}>
            Your reply
          </label>
          <Textarea
            id={`${testIdPrefix}-textarea`}
            data-testid={`${testIdPrefix}-textarea`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Thank you so much, ${name}. It really helped to know you had my back.`}
            maxLength={2000}
            className="mt-2 min-h-[120px] text-lg border-2"
            autoFocus
          />
          <p className="mt-1 text-xs text-slate-500 text-right">{message.length}/2000</p>
        </div>

        {alreadyReplied && (
          <p className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 strokeWidth={2.5} className="h-4 w-4" />
            Last reply sent {new Date(retired.reply_sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </p>
        )}

        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-cancel`}
            className="rounded-full border-2 border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-800 hover:border-slate-500"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !message.trim()}
            data-testid={`${testIdPrefix}-send`}
            className="inline-flex items-center gap-2 rounded-full bg-moss-700 px-6 py-2.5 text-base font-bold text-bone hover:bg-moss-800 disabled:opacity-60"
          >
            <Send strokeWidth={2.5} className="h-4 w-4" />
            {sending ? "Sending..." : "Send thank-you"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
